import { motion } from "framer-motion";

const FloatingElements = () => {
  // Code snippet examples
  const codeSnippets = [
    {
      code: `const app = () => {
  return <div>Hello</div>
}`,
      language: "tsx",
      position: { top: "10%", left: "5%" },
      delay: 0,
    },
    {
      code: `function getData() {
  return fetch('/api')
}`,
      language: "js",
      position: { top: "60%", right: "8%" },
      delay: 0.5,
    },
    {
      code: `<Button
  variant="primary"
  onClick={handleClick}
/>`,
      language: "jsx",
      position: { bottom: "15%", left: "10%" },
      delay: 1,
    },
  ];

  // Mobile app screen mockups (SVG representations)
  const appScreens = [
    {
      position: { top: "25%", right: "5%" },
      delay: 0.3,
      type: "mobile",
    },
    {
      position: { bottom: "25%", right: "15%" },
      delay: 0.8,
      type: "tablet",
    },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Floating Code Blocks */}
      {codeSnippets.map((snippet, index) => (
        <motion.div
          key={`code-${index}`}
          className="absolute"
          style={snippet.position}
          initial={{ opacity: 0, y: 20, rotateX: -15 }}
          animate={{
            opacity: [0.1, 0.15, 0.1],
            y: [0, -20, 0],
            rotateX: [-15, -10, -15],
            rotateY: [0, 5, 0],
          }}
          transition={{
            duration: 8,
            delay: snippet.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <div className="relative bg-card/5 backdrop-blur-md border border-primary/10 rounded-lg p-4 shadow-2xl transform-gpu">
            {/* Code Header */}
            <div className="flex items-center gap-2 mb-2">
              <div className="flex gap-1.5">
                <div className="w-2 h-2 rounded-full bg-red-500/30" />
                <div className="w-2 h-2 rounded-full bg-yellow-500/30" />
                <div className="w-2 h-2 rounded-full bg-green-500/30" />
              </div>
              <span className="text-[8px] text-muted-foreground/50 font-mono">
                {snippet.language}
              </span>
            </div>
            {/* Code Content */}
            <pre className="text-[9px] font-mono text-primary/30 leading-relaxed whitespace-pre">
              {snippet.code}
            </pre>
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-lg blur-xl -z-10" />
          </div>
        </motion.div>
      ))}

      {/* Floating App Screens */}
      {appScreens.map((screen, index) => (
        <motion.div
          key={`screen-${index}`}
          className="absolute"
          style={screen.position}
          initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
          animate={{
            opacity: [0.08, 0.12, 0.08],
            y: [0, -15, 0],
            rotateY: [-20, -15, -20],
            rotateX: [5, 0, 5],
          }}
          transition={{
            duration: 10,
            delay: screen.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          <div className="relative">
            {screen.type === "mobile" ? (
              // Mobile Screen Mockup
              <div className="w-32 h-64 bg-gradient-to-br from-card/5 to-card/10 backdrop-blur-md border border-primary/10 rounded-[2rem] p-2 shadow-2xl transform-gpu">
                <div className="w-full h-full bg-background/5 rounded-[1.5rem] overflow-hidden">
                  {/* Notch */}
                  <div className="h-6 bg-card/20 rounded-b-2xl mx-auto w-20 mb-2" />
                  {/* Content bars */}
                  <div className="space-y-2 p-3">
                    <div className="h-3 bg-primary/10 rounded w-3/4" />
                    <div className="h-3 bg-secondary/10 rounded w-1/2" />
                    <div className="h-20 bg-primary/10 rounded mt-3" />
                    <div className="h-3 bg-primary/10 rounded w-2/3 mt-3" />
                    <div className="h-3 bg-secondary/10 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ) : (
              // Tablet Screen Mockup
              <div className="w-40 h-56 bg-gradient-to-br from-card/5 to-card/10 backdrop-blur-md border border-secondary/10 rounded-2xl p-3 shadow-2xl transform-gpu">
                <div className="w-full h-full bg-background/5 rounded-xl overflow-hidden">
                  {/* Header */}
                  <div className="h-8 bg-card/20 flex items-center px-2 gap-1">
                    <div className="w-2 h-2 bg-primary/20 rounded-full" />
                    <div className="w-2 h-2 bg-secondary/20 rounded-full" />
                    <div className="w-2 h-2 bg-primary/20 rounded-full" />
                  </div>
                  {/* Content */}
                  <div className="p-3 space-y-2">
                    <div className="h-2 bg-primary/10 rounded w-full" />
                    <div className="h-2 bg-secondary/10 rounded w-4/5" />
                    <div className="h-16 bg-primary/10 rounded mt-2" />
                    <div className="grid grid-cols-2 gap-2 mt-2">
                      <div className="h-12 bg-primary/10 rounded" />
                      <div className="h-12 bg-secondary/10 rounded" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-secondary/5 to-primary/5 rounded-2xl blur-2xl -z-10" />
          </div>
        </motion.div>
      ))}

      {/* Additional floating HTML snippets */}
      <motion.div
        className="absolute top-[40%] left-[8%]"
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0.08, 0.12, 0.08],
          x: [0, 10, 0],
          rotateZ: [0, 2, 0],
        }}
        transition={{
          duration: 7,
          delay: 0.2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <div className="bg-card/5 backdrop-blur-md border border-accent/10 rounded-lg p-3 shadow-xl">
          <pre className="text-[8px] font-mono text-accent/30">
            {`<div className="hero">
  <h1>Welcome</h1>
</div>`}
          </pre>
        </div>
      </motion.div>

      {/* CSS snippet */}
      <motion.div
        className="absolute top-[70%] left-[20%]"
        initial={{ opacity: 0 }}
        animate={{
          opacity: [0.08, 0.12, 0.08],
          y: [0, -10, 0],
          rotateZ: [0, -2, 0],
        }}
        transition={{
          duration: 9,
          delay: 1.2,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      >
        <div className="bg-card/5 backdrop-blur-md border border-secondary/10 rounded-lg p-3 shadow-xl">
          <pre className="text-[8px] font-mono text-secondary/30">
            {`.button {
  background: gradient
  padding: 1rem
}`}
          </pre>
        </div>
      </motion.div>

      {/* Small floating icons representing tech stack */}
      <motion.div
        className="absolute top-[15%] left-[25%]"
        animate={{
          y: [0, -15, 0],
          rotate: [0, 360],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <div className="w-8 h-8 bg-primary/5 backdrop-blur-sm border border-primary/20 rounded-lg flex items-center justify-center">
          <span className="text-[10px] font-bold text-primary/40">TS</span>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-[35%] right-[25%]"
        animate={{
          y: [0, 15, 0],
          rotate: [0, -360],
        }}
        transition={{
          duration: 25,
          repeat: Infinity,
          ease: "linear",
        }}
      >
        <div className="w-8 h-8 bg-secondary/5 backdrop-blur-sm border border-secondary/20 rounded-lg flex items-center justify-center">
          <span className="text-[10px] font-bold text-secondary/40">JS</span>
        </div>
      </motion.div>
    </div>
  );
};

export default FloatingElements;
